import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Check, X, Wand2, Users } from "lucide-react";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

/**
 * Manual Heat STRUCTURE Builder — organizer defines only the STRUCTURE
 * (number of leaders / followers per heat). The backend assigns dancers
 * from the ranked pool automatically.
 *
 * Props:
 * - api, headers
 * - competition (object with rounds[])
 * - open, onOpenChange, onSaved
 */
export default function ManualHeatBuilder({ api, headers, competition, open, onOpenChange, onSaved }) {
  const [round, setRound] = useState(1);
  const [heats, setHeats] = useState([{ uid: `h_${Date.now()}_0`, leaders: 5, followers: 5 }]);
  const [saving, setSaving] = useState(false);
  const [pool, setPool] = useState(null); // { leaders: [], followers: [] }
  const [loading, setLoading] = useState(false);
  const [qualMode, setQualMode] = useState("per_heat"); // "per_heat" | "global"
  const [qualPerHeat, setQualPerHeat] = useState(2);
  const [qualTopN, setQualTopN] = useState(10);

  // Initialize state ONCE per open transition — never wipe user input afterwards.
  useEffect(() => {
    if (!open) return;
    const initRound = competition?.current_round && competition.current_round > 0 ? competition.current_round : 1;
    setRound(initRound);
    setHeats([{ uid: `h_${Date.now()}_init`, leaders: 5, followers: 5 }]);
    setSaving(false);
    // Read existing qualification settings from competition (if any)
    const qs = competition?.qualification_settings || {};
    if (qs.mode === "global") {
      setQualMode("global");
      setQualTopN(qs.top_n || 10);
    } else {
      setQualMode("per_heat");
      setQualPerHeat(qs.per_heat || competition?.qualification_per_heat || 2);
    }

    let alive = true;
    setLoading(true);
    axios
      .get(`${api}/competitions/${competition.competition_id}/dancer-pool`, headers)
      .then((r) => {
        if (alive) setPool({ leaders: r.data?.leaders || [], followers: r.data?.followers || [] });
      })
      .catch(() => alive && toast.error("Failed to load dancer pool"))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const totals = useMemo(() => {
    const L = heats.reduce((a, h) => a + (parseInt(h.leaders, 10) || 0), 0);
    const F = heats.reduce((a, h) => a + (parseInt(h.followers, 10) || 0), 0);
    return { L, F };
  }, [heats]);

  const poolCounts = useMemo(() => ({
    L: pool?.leaders?.length || 0,
    F: pool?.followers?.length || 0,
  }), [pool]);

  const overL = totals.L > poolCounts.L;
  const overF = totals.F > poolCounts.F;

  const addHeat = () => setHeats((prev) => [...prev, { uid: `h_${Date.now()}_${prev.length}`, leaders: 5, followers: 5 }]);

  const removeHeat = (idx) => {
    setHeats((prev) => {
      if (prev.length <= 1) {
        toast.error("Need at least 1 heat");
        return prev;
      }
      return prev.filter((_, i) => i !== idx);
    });
  };

  const setField = (idx, field, value) => {
    const n = Math.max(0, parseInt(value, 10) || 0);
    setHeats((prev) => prev.map((h, i) => (i === idx ? { ...h, [field]: n } : h)));
  };

  const submit = async () => {
    if (totals.L === 0 && totals.F === 0) {
      toast.error("Set at least one leader or follower per heat");
      return;
    }
    if (overL || overF) {
      const ok = window.confirm(
        `Pool has only ${poolCounts.L}L / ${poolCounts.F}F.\nYou asked for ${totals.L}L / ${totals.F}F.\nThe system will fill as many as possible; the rest will be empty slots.\n\nContinue?`
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      const r = await axios.post(
        `${api}/competitions/${competition.competition_id}/heats/manual`,
        {
          round_number: round,
          heats: heats.map((h) => ({ leaders: h.leaders, followers: h.followers })),
          mark_advancing: true,
          use_advancing_only: false,
          qualification_per_heat: qualMode === "per_heat" ? qualPerHeat : null,
          qualification_top_n: qualMode === "global" ? qualTopN : null,
        },
        headers
      );
      toast.success(r.data?.message || "Heats created");
      onSaved?.(r.data?.round_number || round);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Playfair_Display'] flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-[#FFD700]" />
            Setup Heats — Structure
          </DialogTitle>
        </DialogHeader>

        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wider text-[#A1A1AA]">Round</span>
            <Select value={String(round)} onValueChange={(v) => setRound(parseInt(v, 10))}>
              <SelectTrigger className="w-44 bg-black/30 border-white/10 text-white" data-testid="manual-round-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(competition?.rounds || []).map((r, i) => (
                  <SelectItem key={r} value={String(i + 1)}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" variant="outline" className="btn-outline" onClick={addHeat} data-testid="manual-add-heat">
            <Plus className="w-3 h-3 mr-1" />
            Add Heat
          </Button>
        </div>

        {/* Pool summary */}
        <div className="mt-3 glass rounded-lg p-3 text-xs flex items-center gap-4">
          <Users className="w-4 h-4 text-[#A1A1AA]" />
          {loading ? (
            <span className="text-[#A1A1AA]"><Loader2 className="w-3 h-3 inline animate-spin mr-1" />Loading pool…</span>
          ) : (
            <>
              <span><b className="text-[#FFD700]">{poolCounts.L}</b> Leaders</span>
              <span><b className="text-[#FF007F]">{poolCounts.F}</b> Followers</span>
              <span className="ml-auto text-[#A1A1AA]">
                You requested <span className={overL ? "text-red-400 font-bold" : "text-white"}>{totals.L}L</span> /
                <span className={overF ? "text-red-400 font-bold ml-1" : "text-white ml-1"}>{totals.F}F</span>
                across {heats.length} heat(s)
              </span>
            </>
          )}
        </div>

        {/* Qualification setup */}
        <div className="mt-3 glass rounded-lg p-3 border border-[#FFD700]/30" data-testid="qualification-card">
          <p className="text-xs uppercase tracking-wider text-[#FFD700] font-bold mb-2">
            How many dancers qualify to the next round?
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setQualMode("per_heat")}
              className={`p-2 rounded-md text-xs border text-left ${
                qualMode === "per_heat"
                  ? "border-[#FFD700] bg-[#FFD700]/10 text-[#FFD700]"
                  : "border-white/10 text-[#A1A1AA] hover:border-white/20"
              }`}
              data-testid="qual-mode-per-heat"
            >
              <p className="font-bold">Top N per heat</p>
              <p className="opacity-70 text-[10px]">Each heat advances same N</p>
            </button>
            <button
              type="button"
              onClick={() => setQualMode("global")}
              className={`p-2 rounded-md text-xs border text-left ${
                qualMode === "global"
                  ? "border-[#FFD700] bg-[#FFD700]/10 text-[#FFD700]"
                  : "border-white/10 text-[#A1A1AA] hover:border-white/20"
              }`}
              data-testid="qual-mode-global"
            >
              <p className="font-bold">Top N globally</p>
              <p className="opacity-70 text-[10px]">Best N across all heats</p>
            </button>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-xs text-[#A1A1AA]">
              {qualMode === "per_heat" ? "Top" : "Top"}
            </span>
            {qualMode === "per_heat" ? (
              <input
                type="number"
                min={1}
                value={qualPerHeat}
                onChange={(e) => setQualPerHeat(Math.max(1, parseInt(e.target.value, 10) || 1))}
                className="w-20 bg-black/40 border border-white/10 rounded px-2 py-1.5 text-white text-xs"
                data-testid="qual-per-heat-input"
              />
            ) : (
              <input
                type="number"
                min={1}
                value={qualTopN}
                onChange={(e) => setQualTopN(Math.max(1, parseInt(e.target.value, 10) || 1))}
                className="w-20 bg-black/40 border border-white/10 rounded px-2 py-1.5 text-white text-xs"
                data-testid="qual-top-n-input"
              />
            )}
            <span className="text-xs text-[#A1A1AA]">
              {qualMode === "per_heat" ? "dancers per heat advance" : "dancers total advance"}
            </span>
          </div>
        </div>

        {/* Heats list */}
        <div className="mt-3 space-y-2">
          {heats.map((h, idx) => (
            <div
              key={h.uid}
              className="glass rounded-lg p-3 flex items-center gap-3 flex-wrap"
              data-testid={`manual-heat-row-${idx + 1}`}
            >
              <div className="font-['Playfair_Display'] font-bold text-white min-w-[80px]">Heat {idx + 1}</div>

              <label className="flex items-center gap-2 text-xs">
                <span className="text-[#FFD700] font-bold uppercase tracking-wider">Leaders</span>
                <input
                  type="number"
                  min={0}
                  value={h.leaders}
                  onChange={(e) => setField(idx, "leaders", e.target.value)}
                  className="w-20 bg-black/40 border border-white/10 rounded px-2 py-1.5 text-white"
                  data-testid={`manual-heat-${idx + 1}-leaders`}
                />
              </label>

              <label className="flex items-center gap-2 text-xs">
                <span className="text-[#FF007F] font-bold uppercase tracking-wider">Followers</span>
                <input
                  type="number"
                  min={0}
                  value={h.followers}
                  onChange={(e) => setField(idx, "followers", e.target.value)}
                  className="w-20 bg-black/40 border border-white/10 rounded px-2 py-1.5 text-white"
                  data-testid={`manual-heat-${idx + 1}-followers`}
                />
              </label>

              <button
                onClick={() => removeHeat(idx)}
                className="ml-auto text-red-400 hover:text-red-300 p-1.5"
                data-testid={`manual-remove-heat-${idx + 1}`}
                disabled={heats.length <= 1}
                aria-label="Remove heat"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <p className="text-[10px] text-[#52525B] mt-3 italic">
          The system will assign dancers automatically by ranking (advancing first, then next-best non-advancing).
          You only define the structure — never pick names.
        </p>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-white/10">
          <Button variant="ghost" className="text-[#A1A1AA]" onClick={() => onOpenChange(false)} disabled={saving} data-testid="manual-cancel">
            <X className="w-3 h-3 mr-1" /> Cancel
          </Button>
          <Button className="btn-gold" onClick={submit} disabled={saving || loading} data-testid="manual-save">
            {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
            Create heats
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
